import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import {
  ArrowLeft, Shield, Users, Key, Activity, Monitor,
  CheckCircle, XCircle, Search, Clock, Globe, Smartphone,
  UserCog, LogIn, BookOpen, FileText, Cpu, Loader2, UserPlus,
  Building2, DollarSign, Ban, Edit3, Wifi, ToggleLeft, ToggleRight,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "monitoring", label: "Monitoring Centre" },
  { key: "innovation", label: "Innovation Centre" },
  { key: "hr", label: "Human Centre (HR)" },
  { key: "learning", label: "Learning Hub" },
  { key: "comms", label: "Comms Centre" },
  { key: "knowledge", label: "Knowledge Hub" },
  { key: "agent", label: "Agent Support" },
  { key: "smart_office", label: "Smart Office" },
  { key: "aipps", label: "Aipps Credits" },
  { key: "alerts", label: "Alerts Hub" },
  { key: "rewards", label: "Rewards Centre" },
  { key: "documents", label: "Documents" },
  { key: "courses", label: "Courses" },
];

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Multi-Tenant state
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);

  // Credit Rates state
  const [creditRates, setCreditRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [editingRate, setEditingRate] = useState(null);

  // Cache Management state
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastCleared, setLastCleared] = useState(null);

  // Create User state
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createUsername, setCreateUsername] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState("member");
  const [creatingUser, setCreatingUser] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  // Transcription Provider settings
  const [transcriptionProvider, setTranscriptionProvider] = useState("local");
  const [assemblyaiConfigured, setAssemblyaiConfigured] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Online Users state
  const [onlineData, setOnlineData] = useState(null);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (activeTab === "settings") {
      fetchTranscriptionProvider();
    }
    if (activeTab === "tenants") {
      fetchTenants();
    }
    if (activeTab === "credit-rates") {
      fetchCreditRates();
    }
    if (activeTab === "cache") {
      fetchCacheStatus();
    }
    if (activeTab === "online") {
      fetchOnlineUsers();
    }
  }, [activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, sessionsRes, auditRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/sessions`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/audit`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSessions(sessionsRes.data);
      setAudit(auditRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(`Role changed to ${newRole}`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  const loadPermissions = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/permissions`, {
        headers: getHeaders(), withCredentials: true
      });
      setUserPermissions(res.data);
      setSelectedUserId(userId);
    } catch (err) {
      toast.error("Failed to load permissions");
    }
  };

  const togglePermission = async (feature, currentValue) => {
    if (!selectedUserId) return;
    const newVal = !currentValue;
    try {
      await axios.put(
        `${API}/admin/users/${selectedUserId}/permissions`,
        { permissions: { [feature]: newVal } },
        { headers: getHeaders(), withCredentials: true }
      );
      setUserPermissions(prev => ({
        ...prev,
        permissions: { ...prev.permissions, [feature]: newVal }
      }));
      // Update local users list
      setUsers(prev => prev.map(u =>
        u.user_id === selectedUserId
          ? { ...u, permissions: { ...u.permissions, [feature]: newVal } }
          : u
      ));
    } catch (err) {
      toast.error("Failed to update permission");
    }
  };

  const fetchTranscriptionProvider = async () => {
    try {
      const res = await axios.get(`${API}/assembly/admin/transcription-provider`, {
        headers: getHeaders(), withCredentials: true,
      });
      setTranscriptionProvider(res.data.provider);
      setAssemblyaiConfigured(res.data.assemblyai_configured);
    } catch {}
  };

  const handleSetTranscriptionProvider = async (provider) => {
    setSettingsLoading(true);
    try {
      await axios.put(`${API}/assembly/admin/transcription-provider`,
        { provider },
        { headers: getHeaders(), withCredentials: true },
      );
      setTranscriptionProvider(provider);
      toast.success(`Transcription provider set to ${provider}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set provider");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createName || !createEmail || !createPassword) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (createPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreatingUser(true);
    setCreateResult(null);
    try {
      const res = await axios.post(`${API}/admin/create-user`, {
        name: createName,
        email: createEmail,
        username: createUsername || undefined,
        password: createPassword,
        role: createRole,
      }, { headers: getHeaders(), withCredentials: true });
      setCreateResult(res.data.user);
      toast.success(`User ${res.data.user.email} created`);
      setCreateName("");
      setCreateEmail("");
      setCreateUsername("");
      setCreatePassword("");
      setCreateRole("member");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
    setCreatingUser(false);
  };

  // ── Multi-Tenant Functions ──
  const fetchTenants = async () => {
    setTenantsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tenants`, { headers: getHeaders(), withCredentials: true });
      setTenants(res.data.items || []);
    } catch (err) {
      toast.error("Failed to load tenants");
    }
    setTenantsLoading(false);
  };

  const fetchTenantDetail = async (tenantId) => {
    try {
      const [detailRes, subRes] = await Promise.all([
        axios.get(`${API}/admin/tenants/${tenantId}`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/tenants/${tenantId}/subscription`, { headers: getHeaders(), withCredentials: true }).catch(() => null),
      ]);
      setTenantDetail({ ...detailRes.data, subscription: subRes?.data });
      setSelectedTenant(tenantId);
    } catch (err) {
      toast.error("Failed to load tenant details");
    }
  };

  const updateTenantStatus = async (tenantId, status) => {
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/status`, { status }, { headers: getHeaders(), withCredentials: true });
      toast.success(`Tenant ${status === "ACTIVE" ? "activated" : status.toLowerCase()}`);
      if (selectedTenant === tenantId) fetchTenantDetail(tenantId);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tenant");
    }
  };

  // ── Credit Rate Functions ──
  const fetchCreditRates = async () => {
    setRatesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/credit-rates`, { headers: getHeaders(), withCredentials: true });
      setCreditRates(res.data.rates || []);
    } catch (err) {
      toast.error("Failed to load credit rates");
    }
    setRatesLoading(false);
  };

  const updateCreditRate = async (featureName, credits) => {
    try {
      const res = await axios.put(`${API}/admin/credit-rates`, { feature_name: featureName, credits_per_unit: parseInt(credits) }, { headers: getHeaders(), withCredentials: true });
      setEditingRate(null);
      toast.success(`${featureName} rate updated to ${credits} ALLs`);
      fetchCreditRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update rate");
    }
  };

  const bulkUpdateRates = async (rates) => {
    try {
      await axios.post(`${API}/admin/credit-rates/bulk`, rates, { headers: getHeaders(), withCredentials: true });
      toast.success("All rates updated");
      fetchCreditRates();
    } catch (err) {
      toast.error("Failed to update rates");
    }
  };

  // ── Cache Management ──

  const fetchCacheStatus = async () => {
    try {
      setCacheLoading(true);
      const { data } = await axios.get(`${API}/admin/cache/status`, { headers: getHeaders(), withCredentials: true });
      setCacheStatus(data);
      setLastCleared(data.last_cleared);
    } catch (err) {
      console.error("Failed to fetch cache status:", err);
    } finally {
      setCacheLoading(false);
    }
  };

  const clearCaches = async () => {
    if (!window.confirm("Clear all expired server caches? This removes stale sessions and expired invitations. Active users will not be affected.")) return;
    try {
      setClearing(true);
      const { data } = await axios.post(`${API}/admin/cache/clear`, {}, { headers: getHeaders(), withCredentials: true });
      toast.success(`Cleared: ${data.results.expired_sessions} sessions, ${data.results.expired_invitations} invites, ${data.results.expired_push_subscriptions} push subs`);
      setLastCleared(data.results.cleared_at);
      fetchCacheStatus();
    } catch (err) {
      toast.error("Failed to clear caches");
    } finally {
      setClearing(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, {
        headers: getHeaders(), withCredentials: true
      });
      setUserDetails(res.data);
      setSelectedUserId(userId);
    } catch (err) {
      toast.error("Failed to load user details");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  // ── Online Users ──
  const fetchOnlineUsers = async () => {
    setOnlineLoading(true);
    try {
      const res = await axios.get(`${API}/admin/online-users`, { headers: getHeaders(), withCredentials: true });
      setOnlineData(res.data);
    } catch (err) {
      toast.error("Failed to fetch online users");
    }
    setOnlineLoading(false);
  };

  const toggleUserActive = async (userId, currentlyDisabled) => {
    setTogglingUserId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/active-status`,
        { is_disabled: !currentlyDisabled },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(`User ${currentlyDisabled ? "enabled" : "disabled"} successfully`);
      fetchOnlineUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle user");
    }
    setTogglingUserId(null);
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
      <TopNav />
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-700 font-['Barlow_Condensed'] text-lg">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-700 mb-4">You need administrator privileges to access this panel.</p>
          <Link to="/dashboard">
            <Button variant="outline" className="rounded-none">Back to Dashboard</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="text-slate-700 hover:text-slate-700 shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-9 h-9 bg-indigo-600 flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Barlow_Condensed'] text-xl font-semibold tracking-tight text-slate-800 truncate">
              ADMIN PANEL
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-5 bg-white border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed'] text-slate-800">
                  {stats?.total_users || 0}
                </p>
                <p className="text-xs text-slate-700 truncate">Total Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-5 bg-white border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center shrink-0">
                <LogIn className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed'] text-slate-800">
                  {stats?.recent_logins_7d || 0}
                </p>
                <p className="text-xs text-slate-700 truncate">Logins (7d)</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-5 bg-white border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed'] text-slate-800">
                  {stats?.total_sessions || 0}
                </p>
                <p className="text-xs text-slate-700 truncate">Total Sessions</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-5 bg-white border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-100 flex items-center justify-center shrink-0">
                <UserCog className="w-5 h-5 text-rose-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl sm:text-3xl font-bold font-['Barlow_Condensed'] text-slate-800">
                  {users.filter(u => u.role === "admin").length}
                </p>
                <p className="text-xs text-slate-700 truncate">Admins</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <TabsList className="bg-white border border-slate-200 rounded-lg p-1 inline-flex w-max sm:w-full">
              <TabsTrigger value="overview" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Shield className="w-4 h-4 mr-2 hidden sm:inline" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="online" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Wifi className="w-4 h-4 mr-2 hidden sm:inline" />
                Online
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Users className="w-4 h-4 mr-2 hidden sm:inline" />
                Users
              </TabsTrigger>
              <TabsTrigger value="permissions" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Key className="w-4 h-4 mr-2 hidden sm:inline" />
                Permissions
              </TabsTrigger>
              <TabsTrigger value="sessions" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Monitor className="w-4 h-4 mr-2 hidden sm:inline" />
                Sessions
              </TabsTrigger>
              <TabsTrigger value="audit" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Activity className="w-4 h-4 mr-2 hidden sm:inline" />
                Audit
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Cpu className="w-4 h-4 mr-2 hidden sm:inline" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="tenants" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Building2 className="w-4 h-4 mr-2 hidden sm:inline" />
                Tenants
              </TabsTrigger>
              <TabsTrigger value="cache" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <Trash2 className="w-4 h-4 mr-2 hidden sm:inline" />
                Cache
              </TabsTrigger>
              <TabsTrigger value="credit-rates" className="rounded-md data-[state=active]:bg-indigo-600 data-[state=active]:text-white whitespace-nowrap">
                <DollarSign className="w-4 h-4 mr-2 hidden sm:inline" />
                Credit Rates
              </TabsTrigger>
            </TabsList>
          </div>
          <div className="mt-2 flex gap-2">
            <Link to="/admin/plans">
              <Button variant="outline" size="sm">
                <DollarSign className="w-4 h-4 mr-2" />
                Subscription Plans
              </Button>
            </Link>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 mb-4">Recent Users</h3>
                <div className="space-y-3">
                  {users.slice(0, 10).map(u => (
                    <div key={u.user_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                        <p className="text-xs text-slate-700 truncate">{u.email}</p>
                      </div>
                      <Badge className={u.role === "admin" ? "bg-indigo-100 text-indigo-700 shrink-0 ml-2" : "bg-slate-100 text-slate-600 shrink-0 ml-2"}>
                        {u.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 mb-4">Recent Activity</h3>
                <div className="space-y-2">
                  {sessions.slice(0, 10).map((s, i) => (
                    <div key={i} className="text-sm text-slate-600 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="font-medium text-slate-700">{s.email || s.user_id?.slice(0, 16)}</span>
                      <span className="text-slate-700 mx-1">•</span>
                      <span className="text-xs text-slate-700">{formatDate(s.login_at)}</span>
                      {s.ip_address && (
                        <>
                          <span className="text-slate-700 mx-1">•</span>
                          <span className="text-xs text-slate-700">{s.ip_address}</span>
                        </>
                      )}
                    </div>
                  ))}
                  {sessions.length === 0 && (
                    <p className="text-sm text-slate-700">No recent activity</p>
                  )}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Online Users Tab */}
          <TabsContent value="online" className="mt-6">
            <Card className="bg-white border-slate-200">
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-emerald-500" />
                  Live Users
                  {onlineData && (
                    <span className="ml-2 text-sm font-normal text-slate-700">
                      ({onlineData.online_count} online · {onlineData.offline_count} offline · {onlineData.total_users} total)
                    </span>
                  )}
                </h3>
              </div>
              {onlineLoading ? (
                <div className="p-10 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
                </div>
              ) : onlineData ? (
                <div className="divide-y divide-slate-100">
                  {/* Online Users */}
                  {onlineData.online_users.length > 0 && (
                    <div className="p-4 sm:p-6">
                      <h4 className="text-sm font-semibold text-emerald-600 mb-3 flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        Online ({onlineData.online_users.length})
                      </h4>
                      <div className="space-y-2">
                        {onlineData.online_users.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between py-2 border border-slate-100 px-3 rounded-md">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-emerald-500 rounded-full shrink-0" />
                                <p className="text-sm font-medium text-slate-700 truncate">{u.name || u.username || u.email}</p>
                                {u.email && <p className="text-xs text-slate-700 hidden sm:block">{u.email}</p>}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 ml-4">
                                <Badge className={u.role === "admin" ? "bg-indigo-100 text-indigo-700 text-[10px]" : "bg-slate-100 text-slate-600 text-[10px]"}>
                                  {u.role}
                                </Badge>
                                <span className="text-[10px] text-slate-700">{u.last_seen ? formatDate(u.last_seen) : "just now"}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              {u.is_disabled && (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">Disabled</Badge>
                              )}
                              <button
                                onClick={() => toggleUserActive(u.user_id, u.is_disabled)}
                                disabled={togglingUserId === u.user_id}
                                className={`px-2 py-1 text-xs rounded border font-medium transition-colors ${
                                  u.is_disabled
                                    ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                    : "border-red-300 text-red-700 hover:bg-red-50"
                                }`}
                              >
                                {togglingUserId === u.user_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : u.is_disabled ? (
                                  "Enable"
                                ) : (
                                  "Disable"
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Offline Users */}
                  {onlineData.offline_users.length > 0 && (
                    <div className="p-4 sm:p-6 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-slate-400 rounded-full" />
                        Offline ({onlineData.offline_users.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {onlineData.offline_users.slice(0, 60).map(u => (
                          <div key={u.user_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="w-1.5 h-1.5 bg-slate-300 rounded-full shrink-0" />
                              <span className="text-xs text-slate-600 truncate">{u.name || u.username || u.email}</span>
                              {u.is_disabled && <Badge className="bg-red-100 text-red-700 text-[10px]">Disabled</Badge>}
                            </div>
                            <button
                              onClick={() => toggleUserActive(u.user_id, u.is_disabled)}
                              disabled={togglingUserId === u.user_id}
                              className={`shrink-0 ml-2 text-[10px] px-1.5 py-0.5 rounded border ${
                                u.is_disabled
                                  ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  : "border-red-200 text-red-500 hover:bg-red-50"
                              }`}
                            >
                              {togglingUserId === u.user_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : u.is_disabled ? "Enable" : "Disable"
                              }
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-10 text-center text-sm text-slate-700">
                  Click the Online tab to load live user status
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="bg-white border-slate-200">
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                    All Users ({filteredUsers.length})
                  </h3>
                  <div className="relative w-full sm:w-64">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-700" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-app">
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">Name</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap hidden sm:table-cell">Email</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">Role</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap hidden md:table-cell">Last Login</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="border-b border-slate-100 hover:bg-app">
                        <td className="px-4 sm:px-6 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-indigo-100 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-indigo-600">
                                {u.name?.charAt(0)?.toUpperCase() || "?"}
                              </span>
                            </div>
                            <span className="font-medium text-slate-700 truncate max-w-[120px] sm:max-w-none">
                              {u.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-slate-700 hidden sm:table-cell truncate max-w-[180px]">
                          {u.email}
                        </td>
                        <td className="px-4 sm:px-6 py-3">
                          <select
                            value={u.role || "member"}
                            onChange={(e) => changeRole(u.user_id, e.target.value)}
                            className="text-xs border border-slate-300 rounded-none px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-xs text-slate-700 hidden md:table-cell">
                          {formatDate(u.last_login_at)}
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setSelectedUserId(u.user_id);
                                loadPermissions(u.user_id);
                                setActiveTab("permissions");
                              }}
                              className="p-1.5 text-slate-700 hover:text-indigo-600 hover:bg-indigo-50"
                              title="Edit Permissions"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => viewUserDetails(u.user_id)}
                              className="p-1.5 text-slate-700 hover:text-emerald-600 hover:bg-emerald-50"
                              title="View Details"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="mt-6">
            <div className="grid gap-6">
              {/* User selector */}
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 mb-4">
                  Select User
                </h3>
                <div className="flex flex-wrap gap-2">
                  {users.map(u => (
                    <button
                      key={u.user_id}
                      onClick={() => loadPermissions(u.user_id)}
                      className={`px-3 py-1.5 text-sm border transition-colors ${
                        selectedUserId === u.user_id
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-slate-600 border-slate-300 hover:border-indigo-300"
                      }`}
                    >
                      {u.name}
                    </button>
                  ))}
                </div>
              </Card>

              {/* Permissions grid */}
              {userPermissions && (
                <Card className="bg-white border-slate-200">
                  <div className="p-4 sm:p-6 border-b border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                          {userPermissions.name}
                        </h3>
                        <p className="text-xs text-slate-700">
                          Role: <Badge className={userPermissions.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}>{userPermissions.role}</Badge>
                          {userPermissions.role === "admin" && (
                            <span className="ml-2 text-indigo-700">(Admin — all access granted)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {FEATURES.map(f => {
                        const enabled = userPermissions.permissions?.[f.key] !== false;
                        const isAdmin = userPermissions.role === "admin";
                        return (
                          <div
                            key={f.key}
                            className={`flex items-center justify-between p-3 border ${
                              enabled ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"
                            }`}
                          >
                            <span className={`text-sm font-medium ${enabled ? "text-emerald-700" : "text-slate-700"}`}>
                              {f.label}
                            </span>
                            <Switch
                              checked={enabled}
                              disabled={isAdmin}
                              onCheckedChange={() => togglePermission(f.key, enabled)}
                              className={enabled ? "data-[state=checked]:bg-emerald-500" : ""}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="mt-6">
            <Card className="bg-white border-slate-200">
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                  Session & Login History
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-app">
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">User</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">Date/Time</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap hidden sm:table-cell">IP Address</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap hidden md:table-cell">Device</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-medium text-slate-600 whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-app">
                        <td className="px-4 sm:px-6 py-3">
                          <span className="text-slate-700 font-medium truncate max-w-[120px] inline-block">
                            {s.email || s.user_id?.slice(0, 16)}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-xs text-slate-700 whitespace-nowrap">
                          {formatDate(s.login_at)}
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-xs text-slate-700 hidden sm:table-cell">
                          <div className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {s.ip_address || "-"}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-xs text-slate-700 hidden md:table-cell truncate max-w-[200px]">
                          <div className="flex items-center gap-1">
                            <Smartphone className="w-3 h-3 shrink-0" />
                            <span className="truncate">{s.user_agent || "-"}</span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3">
                          <Badge className={
                            s.action === "login" ? "bg-emerald-100 text-emerald-700" :
                            s.action === "role_change" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }>
                            {s.action || "login"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {sessions.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-700">
                          No session data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="mt-6">
            <div className="grid gap-6">
              {userDetails && (
                <Card className="bg-white border-slate-200 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                      User Details: {userDetails.user?.name}
                    </h3>
                    <button
                      onClick={() => setUserDetails(null)}
                      className="text-xs text-slate-700 hover:text-slate-600"
                    >
                      Close
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">User ID</p>
                      <p className="text-sm font-mono text-slate-700 break-all">{userDetails.user?.user_id}</p>
                    </div>
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">Email</p>
                      <p className="text-sm text-slate-700">{userDetails.user?.email}</p>
                    </div>
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">Role</p>
                      <Badge className={userDetails.user?.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}>
                        {userDetails.user?.role || "member"}
                      </Badge>
                    </div>
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">Last Login IP</p>
                      <p className="text-sm text-slate-700 font-mono">{userDetails.user?.last_login_ip || "-"}</p>
                    </div>
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">Last Login Device</p>
                      <p className="text-xs text-slate-700 break-all">{userDetails.user?.last_login_ua || "-"}</p>
                    </div>
                    <div className="p-3 bg-app border border-slate-200">
                      <p className="text-xs text-slate-700 mb-1">Joined</p>
                      <p className="text-sm text-slate-700">{formatDate(userDetails.user?.created_at)}</p>
                    </div>
                  </div>

                  {/* Login History */}
                  <h4 className="font-['Barlow_Condensed'] text-base font-semibold text-slate-700 mb-3">
                    Login History
                  </h4>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-app">
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Date</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">IP</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap hidden sm:table-cell">Device</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetails.login_history?.map((s, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{formatDate(s.login_at)}</td>
                            <td className="px-3 py-2 text-xs text-slate-700 font-mono">{s.ip_address || "-"}</td>
                            <td className="px-3 py-2 text-xs text-slate-700 hidden sm:table-cell truncate max-w-[150px]">{s.user_agent || "-"}</td>
                            <td className="px-3 py-2">
                              <Badge className={
                                s.action === "login" ? "bg-emerald-100 text-emerald-700" :
                                s.action === "role_change" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                              }>{s.action || "login"}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Course Activity */}
                  <h4 className="font-['Barlow_Condensed'] text-base font-semibold text-slate-700 mb-3">
                    Course / Quiz Activity
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-app">
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Type</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Score</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap hidden sm:table-cell">Date</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Passed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetails.activity?.map((a, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-3 py-2 text-xs text-slate-600">{a.type || "quiz"}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{a.score ?? "-"}%</td>
                            <td className="px-3 py-2 text-xs text-slate-700 hidden sm:table-cell">{formatDate(a.submitted_at)}</td>
                            <td className="px-3 py-2">
                              {a.passed !== undefined ? (
                                a.passed
                                  ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                                  : <XCircle className="w-4 h-4 text-red-500" />
                              ) : "-"}
                            </td>
                          </tr>
                        ))}
                        {(!userDetails.activity || userDetails.activity.length === 0) && (
                          <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-700">No activity yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Audit Overview */}
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 mb-4">
                  Recent Course Activity
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-app">
                        <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">User</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Course</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap hidden sm:table-cell">Type</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Score</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap hidden md:table-cell">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit?.course_activity?.map((a, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-2 text-xs text-slate-600">{a.user_id?.slice(0, 16)}</td>
                          <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[120px] sm:max-w-[200px]">{a.course_id}</td>
                          <td className="px-3 py-2 text-xs text-slate-600 hidden sm:table-cell">{a.type || "quiz"}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-medium ${(a.score || 0) >= 75 ? "text-emerald-600" : "text-red-600"}`}>
                              {a.score ?? "-"}%
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-700 hidden md:table-cell">{formatDate(a.submitted_at)}</td>
                        </tr>
                      ))}
                      {(!audit?.course_activity || audit.course_activity.length === 0) && (
                        <tr><td colSpan={5} className="px-3 py-4 text-center text-xs text-slate-700">No course activity yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Transcription Provider */}
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-cyan-100 flex items-center justify-center shrink-0">
                    <Cpu className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                      Speech-to-Text Transcription Provider
                    </h3>
                    <p className="text-xs text-slate-700">
                      Choose between local GPU/Whisper models or AssemblyAI cloud API
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-app border border-slate-200">
                  <p className="text-sm text-slate-600 mb-3 font-medium">Active Provider</p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleSetTranscriptionProvider("local")}
                      disabled={settingsLoading}
                      className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors ${
                        transcriptionProvider === "local"
                          ? "bg-cyan-600 text-white"
                          : "bg-white text-slate-600 border border-slate-300 hover:border-cyan-300"
                      }`}
                    >
                      {settingsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Cpu className="w-4 h-4" />
                      )}
                      Local / GPU (Whisper)
                    </button>
                    <button
                      onClick={() => handleSetTranscriptionProvider("assemblyai")}
                      disabled={settingsLoading || !assemblyaiConfigured}
                      className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors ${
                        transcriptionProvider === "assemblyai"
                          ? "bg-green-600 text-white"
                          : "bg-white text-slate-600 border border-slate-300 hover:border-green-300"
                      } ${!assemblyaiConfigured ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {settingsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Cpu className="w-4 h-4" />
                      )}
                      AssemblyAI Cloud
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="text-slate-700">Status:</span>
                    <span className={`font-medium ${transcriptionProvider === "assemblyai" ? "text-green-600" : "text-cyan-600"}`}>
                      {transcriptionProvider === "assemblyai" ? "AssemblyAI" : "Local / GPU"}
                    </span>
                    <span className="text-slate-700 mx-1">|</span>
                    {assemblyaiConfigured ? (
                      <span className="text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> ASSEMBLYAI_API_KEY set
                      </span>
                    ) : (
                      <span className="text-amber-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> ASSEMBLYAI_API_KEY not configured
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-700">
                    <strong>Note:</strong> Provider changes take effect immediately but are only active for the current session.
                    To make the change permanent, set the <code className="bg-amber-100 px-1">TRANSCRIPTION_PROVIDER</code> environment variable
                    to <code className="bg-amber-100 px-1">local</code> or <code className="bg-amber-100 px-1">assemblyai</code> in your Railway service configuration.
                  </p>
                </div>
              </Card>

              {/* Create User */}
              <Card className="bg-white border-slate-200 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center shrink-0">
                    <UserPlus className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                      Create New User
                    </h3>
                    <p className="text-xs text-slate-700">
                      Provision a new account with email, password, and role
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-app border border-slate-200 space-y-3">
                  <input
                    placeholder="Full Name"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <input
                    placeholder="Email"
                    type="email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <input
                    placeholder="Username (optional)"
                    value={createUsername}
                    onChange={(e) => setCreateUsername(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <input
                    placeholder="Password (min 6 chars)"
                    type="password"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <div className="flex items-center gap-3">
                    <select
                      value={createRole}
                      onChange={(e) => setCreateRole(e.target.value)}
                      className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <Button
                      onClick={handleCreateUser}
                      disabled={creatingUser || !createName || !createEmail || !createPassword}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    >
                      {creatingUser ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
                      Create User
                    </Button>
                  </div>
                  {createResult && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700">
                      Created: <strong>{createResult.name}</strong> ({createResult.email}) role: {createResult.role}
                    </div>
                  )}
                </div>
              </Card>

              {/* Refresh hint */}
              <div className="text-center">
                <button
                  onClick={fetchTranscriptionProvider}
                  className="text-xs text-indigo-700 hover:text-indigo-900"
                >
                  Refresh settings
                </button>
              </div>

              {/* ── Geo Block Card ── */}
              <GeoBlockCard API={API} getHeaders={getHeaders} />

            </div>
          </TabsContent>

          {/* ── Tenants Tab ── */}
          <TabsContent value="tenants" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tenant List */}
              <div className="lg:col-span-1">
                <Card className="bg-white border-slate-200">
                  <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">Tenants</h3>
                    {tenantsLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-700" />}
                  </div>
                  <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                    {tenants.length === 0 && !tenantsLoading && (
                      <div className="p-6 text-center text-sm text-slate-700">No tenants found</div>
                    )}
                    {tenants.map(t => (
                      <div key={t.id}
                        className={`p-3 cursor-pointer hover:bg-app transition-colors ${selectedTenant === t.id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''}`}
                        onClick={() => fetchTenantDetail(t.id)}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 truncate">{t.corporate_name || t.tenant_type}</span>
                          <Badge className={t.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : t.status === "SUSPENDED" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}>
                            {t.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {t.subdomain && <span className="text-xs text-slate-700">{t.subdomain}.allbaise.com</span>}
                          <span className="text-xs text-slate-700">{t.tenant_type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Tenant Detail */}
              <div className="lg:col-span-2">
                {!tenantDetail ? (
                  <Card className="bg-white border-slate-200 p-8 text-center">
                    <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                    <p className="text-slate-700">Select a tenant to view details</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    <Card className="bg-white border-slate-200 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                            {tenantDetail.tenant.corporate_name || tenantDetail.tenant.tenant_type}
                          </h3>
                          {tenantDetail.tenant.subdomain && (
                            <p className="text-sm text-slate-700">{tenantDetail.tenant.subdomain}.allbaise.com</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {tenantDetail.tenant.status === "ACTIVE" ? (
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                              onClick={() => updateTenantStatus(tenantDetail.tenant.id, "SUSPENDED")}>
                              <Ban className="w-3 h-3 mr-1" /> Suspend
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs"
                              onClick={() => updateTenantStatus(tenantDetail.tenant.id, "ACTIVE")}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Activate
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="p-3 bg-app rounded">
                          <p className="text-xs text-slate-700">Type</p>
                          <p className="font-medium text-slate-700">{tenantDetail.tenant.tenant_type}</p>
                        </div>
                        <div className="p-3 bg-app rounded">
                          <p className="text-xs text-slate-700">Users</p>
                          <p className="font-medium text-slate-700">{tenantDetail.stats?.user_count || 0}</p>
                        </div>
                        <div className="p-3 bg-app rounded">
                          <p className="text-xs text-slate-700">Credits</p>
                          <p className="font-medium text-slate-700">{tenantDetail.stats?.total_credits || 0} ALLs</p>
                        </div>
                        <div className="p-3 bg-app rounded">
                          <p className="text-xs text-slate-700">Plan</p>
                          <p className="font-medium text-slate-700">
                            {tenantDetail.subscription?.subscription?.plan_id || "None"}
                          </p>
                        </div>
                      </div>
                      {tenantDetail.subscription?.subscription && (
                        <div className="mt-2 text-xs text-slate-500">
                          Status: {tenantDetail.subscription.subscription.status}
                          {tenantDetail.subscription.plan?.name && ` · ${tenantDetail.subscription.plan.name}`}
                        </div>
                      )}
                    </Card>

                    {/* Tenant Users */}
                    <Card className="bg-white border-slate-200">
                      <div className="p-4 border-b border-slate-200">
                        <h4 className="font-['Barlow_Condensed'] text-base font-semibold text-slate-700">Users</h4>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                        {tenantDetail.users?.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between px-4 py-2.5">
                            <div>
                              <p className="text-sm font-medium text-slate-700">{u.name || u.email}</p>
                              <p className="text-xs text-slate-700">{u.email}</p>
                            </div>
                            <Badge className={u.role === "TENANT_ADMIN" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}>
                              {u.role}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── Credit Rates Tab ── */}
          <TabsContent value="credit-rates" className="mt-6">
            <Card className="bg-white border-slate-200">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">Credit Rates Configuration</h3>
                  <p className="text-xs text-slate-700">Configure ALLs credit costs per service feature</p>
                </div>
                {ratesLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-700" />}
              </div>
              <div className="p-5">
                {/* Exchange Rate Info */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-xs text-amber-700 font-medium mb-1">ALLs Credit Exchange Rates</p>
                  <p className="text-xs text-amber-600">1000 ALLs = R10 ZAR | $0.50 USD | €0.45 EUR | £0.40 GBP</p>
                </div>

                {/* Rates Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-app">
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Feature</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Unit</th>
                        <th className="text-right px-4 py-3 font-medium text-slate-600">Cost (ALLs)</th>
                        <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditRates.length === 0 && !ratesLoading && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-700">No rates configured. Run database migration.</td></tr>
                      )}
                      {creditRates.map(rate => (
                        <tr key={rate.feature_name} className="border-b border-slate-100 hover:bg-app">
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {rate.feature_name.replace(/_/g, ' ')}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {rate.feature_name === "MEETING_PER_HOUR" ? "per hour" :
                             rate.feature_name === "TRANSCRIBE_PER_MINUTE" ? "per minute" :
                             rate.feature_name === "WORKDOCS_PER_DOC" ? "per document" :
                             rate.feature_name === "AI_EDITOR_PER_REQUEST" ? "per request" :
                             rate.feature_name === "SMARTMAIL_PER_MONTH" ? "per month" : "per unit"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {editingRate === rate.feature_name ? (
                              <div className="flex items-center justify-end gap-1">
                                <input type="number" min={0} defaultValue={rate.credits_per_unit}
                                  className="w-20 text-right border border-slate-300 rounded px-2 py-1 text-sm"
                                  id={`rate-${rate.feature_name}`} />
                                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-500 text-white h-7 text-xs px-2"
                                  onClick={() => {
                                    const el = document.getElementById(`rate-${rate.feature_name}`);
                                    updateCreditRate(rate.feature_name, el?.value || 0);
                                  }}>
                                  Save
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs"
                                  onClick={() => setEditingRate(null)}>
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <span className="font-semibold text-slate-900">{rate.credits_per_unit} ALLs</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="ghost" className="text-slate-700 hover:text-indigo-600"
                              onClick={() => setEditingRate(rate.feature_name)}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Cache Management Tab */}
          <TabsContent value="cache" className="mt-6">
            <Card className="bg-white border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">Cache Management</h3>
                  <p className="text-sm text-slate-700">Clear stale server data and expired records</p>
                </div>
                <Button
                  onClick={clearCaches}
                  disabled={clearing}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  {clearing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clearing...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" /> Clear All Caches</>
                  )}
                </Button>
              </div>

              {cacheLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : cacheStatus ? (
                <div className="space-y-6">
                  {/* Last Cleared */}
                  {lastCleared && (
                    <div className="text-xs text-slate-700">
                      Last cleared: {new Date(lastCleared).toLocaleString()}
                    </div>
                  )}

                  {/* Stale Data */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Stale Data to Clear</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded bg-red-50 border border-red-100">
                        <p className="text-2xl font-bold text-red-600">{cacheStatus.stale_counts?.expired_sessions || 0}</p>
                        <p className="text-xs text-slate-700">Expired Sessions</p>
                      </div>
                      <div className="p-4 rounded bg-red-50 border border-red-100">
                        <p className="text-2xl font-bold text-red-600">{cacheStatus.stale_counts?.expired_invitations || 0}</p>
                        <p className="text-xs text-slate-700">Expired Invitations</p>
                      </div>
                    </div>
                  </div>

                  {/* Total Records */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Database Totals</h4>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 rounded bg-slate-50 border border-slate-200">
                        <p className="text-2xl font-bold text-slate-800">{cacheStatus.total_counts?.users || 0}</p>
                        <p className="text-xs text-slate-700">Users</p>
                      </div>
                      <div className="p-4 rounded bg-slate-50 border border-slate-200">
                        <p className="text-2xl font-bold text-slate-800">{cacheStatus.total_counts?.tenants || 0}</p>
                        <p className="text-xs text-slate-700">Tenants</p>
                      </div>
                      <div className="p-4 rounded bg-slate-50 border border-slate-200">
                        <p className="text-2xl font-bold text-slate-800">{cacheStatus.total_counts?.sessions || 0}</p>
                        <p className="text-xs text-slate-700">Sessions</p>
                      </div>
                      <div className="p-4 rounded bg-slate-50 border border-slate-200">
                        <p className="text-2xl font-bold text-slate-800">{cacheStatus.total_counts?.invitations || 0}</p>
                        <p className="text-xs text-slate-700">Invitations</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-700">
                  Click "Clear All Caches" to get started. Cache status will appear here.
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ── Geo Block Card Component ──
function GeoBlockCard({ API, getHeaders }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [whitelisting, setWhitelisting] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/geo-block`, { headers: getHeaders(), withCredentials: true });
      setConfig(res.data);
    } catch { toast.error("Failed to load geo-block config"); }
    setLoading(false);
  };

  useEffect(() => { fetchConfig(); }, []);

  const toggleEnabled = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/geo-block`, { ...config, enabled: !config.enabled }, { headers: getHeaders(), withCredentials: true });
      setConfig(prev => ({ ...prev, enabled: !prev.enabled }));
      toast.success(`Geo-blocking ${!config.enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  const addCountry = async (code) => {
    if (!config || config.allowed_countries.includes(code)) return;
    setSaving(true);
    try {
      const updated = [...config.allowed_countries, code];
      await axios.put(`${API}/admin/geo-block`, { ...config, allowed_countries: updated }, { headers: getHeaders(), withCredentials: true });
      setConfig(prev => ({ ...prev, allowed_countries: updated }));
      toast.success(`Added ${code} to allowed countries`);
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  const removeCountry = async (code) => {
    if (!config) return;
    setSaving(true);
    try {
      const updated = config.allowed_countries.filter(c => c !== code);
      await axios.put(`${API}/admin/geo-block`, { ...config, allowed_countries: updated }, { headers: getHeaders(), withCredentials: true });
      setConfig(prev => ({ ...prev, allowed_countries: updated }));
      toast.success(`Removed ${code}`);
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  const whitelistMyIp = async () => {
    setWhitelisting(true);
    try {
      const res = await axios.post(`${API}/admin/geo-block/whitelist-my-ip`, {}, { headers: getHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchConfig();
    } catch { toast.error("Failed to whitelist IP"); }
    setWhitelisting(false);
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-slate-700" />
          <span className="text-sm text-slate-700">Loading geo-block config...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-slate-200 p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-red-100 flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
            Geo-Blocking
          </h3>
          <p className="text-xs text-slate-700">
            Restrict access by country — only your IP / South Africa allowed
          </p>
        </div>
      </div>

      {/* Toggle */}
      <div className="mt-4 p-4 bg-app border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">Enable Geo-Blocking</p>
            <p className="text-xs text-slate-700">
              {config?.enabled
                ? "Access is restricted to allowed countries and whitelisted IPs"
                : "All IP addresses can access the site"}
            </p>
          </div>
          <button
            onClick={toggleEnabled}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config?.enabled ? "bg-red-600" : "bg-slate-300"
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              config?.enabled ? "translate-x-6" : "translate-x-1"
            }`} />
          </button>
        </div>
      </div>

      {config?.enabled && (
        <>
          {/* Allowed Countries */}
          <div className="mt-4 p-4 bg-app border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-2">Allowed Countries</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {config?.allowed_countries?.map(code => (
                <span key={code} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-2.5 py-1 rounded">
                  {code}
                  <button onClick={() => removeCountry(code)} className="text-emerald-500 hover:text-emerald-700 ml-1">&times;</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <CountrySelect onSelect={addCountry} />
            </div>
          </div>

          {/* Whitelisted IPs */}
          <div className="mt-4 p-4 bg-app border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-2">Whitelisted IPs</p>
            {config?.whitelist_ips?.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {config.whitelist_ips.map(ip => (
                  <span key={ip} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-1 rounded font-mono">
                    {ip}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-700 mb-3">No whitelisted IPs</p>
            )}
            <button onClick={whitelistMyIp} disabled={whitelisting}
              className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-medium">
              {whitelisting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Whitelist My Current IP
            </button>
          </div>

          <div className="mt-3 p-3 bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700">
              <strong>Note:</strong> Your own IP must be whitelisted or in an allowed country before enabling,
              or you'll lock yourself out. Use "Whitelist My Current IP" first.
            </p>
          </div>
        </>
      )}
    </Card>
  );
}

// Simple country code selector
function CountrySelect({ onSelect }) {
  const COUNTRIES = [
    { code: "ZA", name: "South Africa" },
    { code: "US", name: "United States" },
    { code: "GB", name: "United Kingdom" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "KE", name: "Kenya" },
    { code: "NG", name: "Nigeria" },
    { code: "GH", name: "Ghana" },
    { code: "BW", name: "Botswana" },
    { code: "ZW", name: "Zimbabwe" },
    { code: "MZ", name: "Mozambique" },
    { code: "AO", name: "Angola" },
    { code: "CM", name: "Cameroon" },
    { code: "EG", name: "Egypt" },
    { code: "MA", name: "Morocco" },
    { code: "SN", name: "Senegal" },
    { code: "TZ", name: "Tanzania" },
    { code: "UG", name: "Uganda" },
    { code: "ZM", name: "Zambia" },
    { code: "NA", name: "Namibia" },
    { code: "LS", name: "Lesotho" },
    { code: "SZ", name: "Eswatini" },
  ];
  const [value, setValue] = useState("");

  return (
    <div className="flex items-center gap-2">
      <select value={value} onChange={e => { setValue(e.target.value); if (e.target.value) onSelect(e.target.value); }}
        className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500">
        <option value="">Add country...</option>
        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
      </select>
    </div>
  );
}